import { Navigate, useLocation } from "react-router-dom";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import {
	appendEmbedPortalQueryIfMissing,
	isEmbedPortalSearch,
	searchFromPath,
} from "@constants/embedPortal.config";
import { parseInternalRedirectParam } from "@utils/internalNavigation";
import { FACULTY_HOME_ROUTE, isFacultyUser } from "@utils/facultyAccess";
import { useSelector } from "react-redux";
import type { FC, ReactNode } from "react";
import type { RootState } from "../../store/store";

interface GuestGuardProps {
	children: ReactNode;
}

/** Drop `redirect` so the default dashboard URL is not stuck with a stale return path. */
function searchWithoutRedirect(search: string): string {
	if (!search) return "";
	const normalized = search.startsWith("?") ? search.slice(1) : search;
	const params = new URLSearchParams(normalized);
	params.delete("redirect");
	const rest = params.toString();
	return rest ? `?${rest}` : "";
}

function authRedirectDebug(message: string, data: Record<string, unknown>): void {
	if (import.meta.env.DEV) {
		console.debug("[authRedirect]", message, data);
	}
}

const GuestGuard: FC<GuestGuardProps> = ({ children }) => {
	const { isAuthenticated, user, token } = useSelector((state: RootState) => state.user);
	const location = useLocation();

	if (!isAuthenticated) {
		return <>{children}</>;
	}

	const { pathname, search } = location;
	authRedirectDebug("GuestGuard authenticated", { pathname, search });

	// After login, Redux updates before login mutation's navigate(); this guard was sending everyone
	// to institute-stats-dashboard + full ?search, ignoring ?redirect= on /sign-in.
	if (pathname === BROWSER_ROUTES.LOGIN) {
		const internal = parseInternalRedirectParam(search);
		if (internal && isEmbedPortalSearch(search)) {
			const to = isEmbedPortalSearch(searchFromPath(internal))
				? internal
				: appendEmbedPortalQueryIfMissing(internal);
			authRedirectDebug("GuestGuard -> redirect param", { internal, to });
			return <Navigate to={to} replace />;
		}
	}

	const defaultHome = isFacultyUser(user, token) ? FACULTY_HOME_ROUTE : BROWSER_ROUTES.INSTITUTE_STATS_DASHBOARD;
	const to = isEmbedPortalSearch(search)
		? `${defaultHome}${searchWithoutRedirect(search)}`
		: defaultHome;
	authRedirectDebug("GuestGuard -> default dashboard", { to });
	return <Navigate to={to} replace />;
};

export default GuestGuard;
